import { useCallback, useEffect, useState } from 'react';

import { generatePath, useNavigate } from 'react-router-dom';

import { useIntl } from 'shared/components/Intl';
import { AlertDialog } from 'shared/components/AlertDialog';
import { ROUTES } from 'shared/constants';
import { useGetFormattedSettings } from 'shared/hooks/useGetFormattedSettings';
import { useAccessibleAccounts } from 'shared/providers/AccessibleAccounts';
import { getReEnrolmentYear } from 'shared/utils/getReEnrolmentYear';

import {
  useIsReEnrolmentRoutesPath,
  useReEnrolmentActionRequiredSessionStorage
} from './ReEnrolmentActionRequired.hooks';

export const ReEnrolmentActionRequired = () => {
  const [isActionRequiredModalOpen, setIsActionRequiredModalOpen] = useState(false);
  const navigate = useNavigate();

  const isReEnrolmentRoutesPath = useIsReEnrolmentRoutesPath();
  const { storedValue, setStoredValue } = useReEnrolmentActionRequiredSessionStorage();

  const { formatMessage } = useIntl();
  const { reEnrolment } = useGetFormattedSettings();
  const { paymentRoutesDisabled } = useAccessibleAccounts();

  // we not showing action required popup if already visited reEnrolment pages or we do not show payment pages
  useEffect(() => {
    if ((isReEnrolmentRoutesPath || paymentRoutesDisabled) && !storedValue) {
      setStoredValue(true);
    }
  }, [isReEnrolmentRoutesPath, storedValue, paymentRoutesDisabled, setStoredValue]);

  useEffect(() => {
    if (
      reEnrolment?.hasPendingContracts &&
      !isReEnrolmentRoutesPath &&
      !storedValue &&
      !paymentRoutesDisabled
    ) {
      setIsActionRequiredModalOpen(true);
      setStoredValue(true);
    }
  }, [
    reEnrolment?.hasPendingContracts,
    isReEnrolmentRoutesPath,
    storedValue,
    paymentRoutesDisabled,
    setStoredValue
  ]);

  const handleCloseActionRequiredModal = useCallback(() => setIsActionRequiredModalOpen(false), []);

  const handleGoToReEnrolments = useCallback(() => {
    navigate(generatePath(`${ROUTES.HOME}${ROUTES.RE_ENROLMENT}`));

    setIsActionRequiredModalOpen(false);
  }, [navigate]);

  const currentYear = getReEnrolmentYear();

  return (
    <AlertDialog
      open={isActionRequiredModalOpen}
      title={formatMessage({ id: 'reEnrolmentActionRequired.title' }, { year: currentYear })}
      description={formatMessage(
        {
          id: 'reEnrolmentActionRequired.description'
        },
        { year: currentYear }
      )}
      cancelBtnText={formatMessage({ id: 'reEnrolmentActionRequired.cancel' })}
      confirmBtnText={formatMessage({ id: 'reEnrolmentActionRequired.confirm' })}
      onCancel={handleCloseActionRequiredModal}
      onConfirm={handleGoToReEnrolments}
      onClose={handleCloseActionRequiredModal}
      isError={false}
      fullWidth
    />
  );
};
